import {
  createFileRoute,
  useNavigate,
  Link,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ImageGalleryCard } from "@/components/image-gallery-card";
import { Button } from "@/components/ui/button";
import { GallerySkeleton } from "@/components/skeletons";
import { getServerGalleryGenerations } from "@/lib/server-data";
import { Generation } from "@/types/generation";

interface GallerySearch {
  page?: number;
}

export const Route = createFileRoute("/_authenticated/gallery")({
  validateSearch: (search: Record<string, unknown>): GallerySearch => {
    return {
      page: Number(search.page) || 1,
    };
  },

  // No loader - all data fetched client-side
  component: GalleryPage,
});

function GalleryPage() {
  const navigate = useNavigate();
  const { session } = Route.useRouteContext();
  const { page = 1 } = Route.useSearch();

  // Fetch gallery data client-side with pagination
  // Note: getServerGalleryGenerations extracts page from URL query params
  const { data, isPending: isLoading } = useQuery({
    queryKey: ['gallery-generations', session?.user?.id, page],
    queryFn: () =>
      getServerGalleryGenerations({
        data: { userId: session!.user.id },
      }),
    staleTime: 5 * 60 * 1000,
  });

  // Show skeleton during loading
  if (isLoading) {
    return <GallerySkeleton />;
  }

  const handlePageChange = (newPage: number) => {
    navigate({ to: "/gallery", search: { page: newPage } });
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="rounded-full">
              ← Back to Dashboard
            </Button>
          </Link>
        </div>

        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Gallery</h1>
            <p className="text-sm text-muted-foreground">
              {isLoading
                ? "Loading..."
                : `${data?.pagination.total || 0} total images`}
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="aspect-square animate-pulse rounded-xl bg-muted"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {data?.data.map((generation: Generation) => (
                <ImageGalleryCard key={generation.id} generation={generation} />
              ))}
            </div>
          )}

          {!isLoading && data && data.data.length === 0 && (
            <div className="flex min-h-[400px] items-center justify-center rounded-xl border-2 border-dashed border-border">
              <div className="text-center space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  No images yet
                </p>
              </div>
            </div>
          )}

          {data && data.pagination.totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                disabled={!data.pagination.hasPrev}
                onClick={() => handlePageChange(page - 1)}
                className="rounded-full"
              >
                Previous
              </Button>

              <span className="text-sm text-muted-foreground px-4">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </span>

              <Button
                variant="outline"
                disabled={!data.pagination.hasNext}
                onClick={() => handlePageChange(page + 1)}
                className="rounded-full"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
